import { z } from 'zod';

/**
 * GSC-driven opportunity + content-decay types (A2 + A3).
 *
 * Scopes required on the Google OAuth grant:
 *   - https://www.googleapis.com/auth/webmasters.readonly  — Search Analytics
 *   - https://www.googleapis.com/auth/analytics.readonly    — GA4 (sibling)
 *
 * URL Inspection (optional, used lightly in audits) also works under
 * webmasters.readonly for read-only index status.
 */

export const GscOpportunitySchema = z.object({
  query: z.string(),
  page: z.string().nullable().optional(),
  impressions: z.number(),
  clicks: z.number(),
  ctr: z.number(),
  position: z.number(),
});
export type GscOpportunity = z.infer<typeof GscOpportunitySchema>;

export const GscDecayItemSchema = z.object({
  page: z.string(),
  impressions_current: z.number(),
  impressions_prior: z.number(),
  drop_pct: z.number(),
  clicks_current: z.number(),
  clicks_prior: z.number(),
  position_current: z.number().nullable(),
  position_prior: z.number().nullable(),
});
export type GscDecayItem = z.infer<typeof GscDecayItemSchema>;

export const GscInsightsResponseSchema = z.object({
  connected: z.boolean(),
  configured: z.boolean(),
  site_url: z.string().nullable(),
  fetched_at: z.string().nullable(),
  cached: z.boolean(),
  period: z
    .object({
      current_start: z.string(),
      current_end: z.string(),
      prior_start: z.string(),
      prior_end: z.string(),
    })
    .nullable(),
  totals: z
    .object({
      clicks: z.number(),
      impressions: z.number(),
      ctr: z.number(),
      position: z.number(),
    })
    .nullable(),
  opportunities: z.array(GscOpportunitySchema),
  decay: z.array(GscDecayItemSchema),
  warnings: z.array(
    z.object({
      code: z.string(),
      message: z.string(),
    }),
  ),
});
export type GscInsightsResponse = z.infer<typeof GscInsightsResponseSchema>;

export const CreateGscRefreshBriefRequestSchema = z.object({
  page_url: z.string().url(),
  /** Optional override; defaults to a refresh-oriented keyword derived from the URL path. */
  target_keyword: z.string().min(2).max(200).optional(),
  delivery: z.literal('markdown').default('markdown'),
});
export type CreateGscRefreshBriefRequest = z.infer<typeof CreateGscRefreshBriefRequestSchema>;

/** Thresholds used by opportunities + decay detection. */
export const GSC_INSIGHTS_THRESHOLDS = {
  /** Min impressions in the lookback window for an opportunity. */
  opportunityMinImpressions: 100,
  /** Avg position worse than this → opportunity. */
  opportunityMinPosition: 10,
  /** WoW impression drop fraction (0.20 = 20%). */
  decayDropFraction: 0.2,
  /** Min prior-week impressions before a page is considered for decay. */
  decayMinPriorImpressions: 50,
  /** How many opportunity/decay rows to return. */
  limit: 25,
} as const;
